import { Fragment } from "react";
import { ChevronDown } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTip, Legend, ResponsiveContainer } from "recharts";
import { SectionHeader } from "../SectionHeader";
import RAGBadge from "../RAGBadge";
import ThemeChip from "../ThemeChip";
import { fmtCr, fmtDate } from "../../utils/format";

export function GranteeTable({
  granteeList, page, setPage,
  expandedGrantees, expandLoading, toggleGranteeExpand,
  selectedGrantee, handleGranteeTrendClick,
  fyTrend, trendLoading,
}) {
  return (
    <>
      {/* FY Trend chart — shown when a grantee is selected */}
      {selectedGrantee && (
        <SectionHeader title={`FY Trend — ${selectedGrantee}`} subtitle="Disbursement & KPI achievement by financial year">
          {trendLoading ? (
            <div className="h-40 flex items-center justify-center text-slate-600 text-sm">Loading trend…</div>
          ) : fyTrend.length > 0 ? (
            <div className="h-56" data-testid="fy-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={fyTrend} margin={{ top: 4, right: 40, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="fy" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="left" tickFormatter={v => `₹${Number(v).toFixed(2)}`} tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} />
                  <RechartsTip
                    formatter={(v, n) => n === "avg_kpi_pct" ? [`${Number(v).toFixed(1)}%`, "Avg KPI"] : [`₹${Number(v).toFixed(2)} Cr`, "Disbursed"]}
                    contentStyle={{ backgroundColor: "#1E293B", border: "none", borderRadius: 8, color: "#fff", fontSize: 11 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 10 }} formatter={v => v === "disbursed_cr" ? "Disbursed (₹ Cr)" : "Avg KPI Achievement %"} />
                  <Bar yAxisId="left" dataKey="disbursed_cr" fill="#006837" radius={[3, 3, 0, 0]} name="disbursed_cr" />
                  <Line yAxisId="right" type="monotone" dataKey="avg_kpi_pct" stroke="#F59E0B" strokeWidth={2.5}
                    dot={{ r: 4, fill: "#F59E0B" }} name="avg_kpi_pct" connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-24 flex items-center justify-center text-slate-600 text-sm">No FY trend data available</div>
          )}
        </SectionHeader>
      )}

      {/* Grantee List Table */}
      <SectionHeader title={`Grantee List (${granteeList.total})`} subtitle="Click any row to expand grants or see FY trend">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="grantee-list-table">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left text-xs font-medium text-slate-500 uppercase py-2 pr-4">#</th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase py-2">Grantee</th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase py-2">Theme</th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Grants</th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Sanctioned</th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Disbursed</th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Utilised</th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase py-2 whitespace-nowrap group relative cursor-help">
                  <span className="border-b border-dotted border-slate-400">Health Score</span>
                  <div className="absolute right-0 top-full mt-1 w-72 bg-slate-800 text-white text-xs font-normal normal-case rounded-lg p-3 opacity-0 group-hover:opacity-100 pointer-events-none z-50 shadow-xl transition-opacity text-left">
                    <div className="font-semibold mb-2">Health Score (Interim Formula)</div>
                    <div className="space-y-1.5">
                      <div><strong>Formula:</strong> KPI (57%) + Utilisation (43%)</div>
                      <div className="text-slate-500">If KPI unavailable: Disbursement (55%) + Util (45%)</div>
                      <div className="border-t border-slate-600 pt-1.5 mt-1.5">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-green-500"></span>
                          <span>Green: ≥70%</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                          <span>Amber: 35-69%</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-red-500"></span>
                          <span>Red: &lt;35%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {granteeList.data.map((r, i) => {
                const isExpanded = expandedGrantees[r.grantee] !== undefined;
                const isLoading = expandLoading[r.grantee];
                const grantDetails = expandedGrantees[r.grantee] || [];
                return (
                  <Fragment key={`grantee-${r.grantee}`}>
                    <tr
                      className={`border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${isExpanded ? "bg-red-50/20" : ""}`}
                      onClick={() => toggleGranteeExpand(r.grantee, r.grants, r.theme)}
                      data-testid={`grantee-row-${i}`}
                    >
                      <td className="py-2.5 text-xs text-slate-600 pr-4">{(page - 1) * 20 + i + 1}</td>
                      <td className="py-2.5 font-medium text-slate-900 text-sm">
                        <div className="flex items-center gap-1.5">
                          <ChevronDown size={13} className={`text-slate-600 flex-shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""} ${isLoading ? "animate-spin" : ""}`} />
                          <span className="hover:text-[#006837]" onClick={e => { e.stopPropagation(); handleGranteeTrendClick(r.grantee); }}>{r.grantee}</span>
                        </div>
                      </td>
                      <td className="py-2.5"><ThemeChip theme={r.theme} /></td>
                      <td className="py-2.5 text-right text-xs">{r.grants}</td>
                      <td className="py-2.5 text-right text-xs text-slate-600">{r.allocation_cr ? fmtCr(r.allocation_cr) : "–"}</td>
                      <td className="py-2.5 text-right text-xs font-semibold text-[#006837]">{fmtCr(r.disbursed_cr)}</td>
                      <td className="py-2.5 text-right text-xs text-slate-600">{r.utilised_cr ? fmtCr(r.utilised_cr) : "–"}</td>
                      <td className="py-2.5 text-right">
                        {r.health_score ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <RAGBadge status={r.health_score.rag} size="sm" />
                            <span className="text-xs text-slate-500">{r.health_score.score}%</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-500">–</span>
                        )}
                      </td>
                    </tr>
                    {isExpanded && grantDetails.length > 0 && (
                      <tr key={`subhdr-${i}`} className="bg-slate-100/70">
                        <td className="py-1.5 pl-8 text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Grant ID</td>
                        <td className="py-1.5 text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Project Title</td>
                        <td className="py-1.5 text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Theme</td>
                        <td className="py-1.5 text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Programme Officer</td>
                        <td className="py-1.5 text-right text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Sanctioned</td>
                        <td className="py-1.5 text-right text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Disbursed</td>
                        <td className="py-1.5 text-right text-[10px] text-slate-500 font-semibold uppercase tracking-wide">KPI %</td>
                        <td className="py-1.5 text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Fund · Health</td>
                      </tr>
                    )}
                    {isExpanded && grantDetails.map((g, gi) => (
                      <tr key={`detail-${i}-${gi}`} className="bg-slate-50/80 border-b border-slate-100" data-testid={`grant-detail-${i}-${gi}`}>
                        <td className="py-2 pl-8 text-xs text-slate-600 font-mono whitespace-nowrap">{g.grant_id}</td>
                        <td className="py-2 text-xs text-slate-700 max-w-[200px]">
                          <div className="truncate font-medium" title={g.project_title}>{g.project_title}</div>
                          <div className="text-slate-600 text-[10px] mt-0.5">{g.fy} · {g.status}</div>
                        </td>
                        <td className="py-2"><ThemeChip theme={g.theme} /></td>
                        <td className="py-2 text-xs text-slate-700 whitespace-nowrap">{g.po}</td>
                        <td className="py-2 text-right text-xs text-slate-600">{g.allocation_cr ? fmtCr(g.allocation_cr) : "–"}</td>
                        <td className="py-2 text-right text-xs font-semibold text-[#006837]">{fmtCr(g.disbursed_cr)}</td>
                        <td className="py-2 text-right text-xs">
                          {g.kpi_pct != null ? (
                            <span className="font-medium text-slate-700">{g.kpi_pct}%
                              <span className="text-[10px] text-slate-600 ml-1">({g.kpi_count})</span>
                            </span>
                          ) : <span className="text-slate-500">—</span>}
                        </td>
                        <td className="py-2">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-[10px] font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded w-fit">{g.trust}</span>
                            {g.rag && <RAGBadge status={g.rag} size="sm" />}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {isExpanded && grantDetails.length > 0 && (
                      <tr key={`subfoot-${i}`} className="bg-white border-b-2 border-slate-200">
                        <td colSpan={8} className="py-1 pl-8 text-[10px] text-slate-600 italic">
                          {grantDetails.length} grants shown · matching current filters · KPI % = avg MEAS indicator achievement
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {granteeList.total > 20 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
              <span className="text-xs text-slate-500">
                {(page - 1) * 20 + 1}–{Math.min(page * 20, granteeList.total)} of {granteeList.total}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="text-xs px-3 py-1.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40">Prev</button>
                <button onClick={() => setPage(p => p + 1)} disabled={page * 20 >= granteeList.total}
                  className="text-xs px-3 py-1.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </div>
      </SectionHeader>
    </>
  );
}
